import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { SEU_NEW_ID } from '@seu-helpers';
import { ConfigFormService } from '@seu-services';
import {
  SEU_GLOBAL_VALUES_EMISSION_LIMITS_CONFIG_KEY,
  SEU_GLOBAL_VALUES_OPEX_CONFIG_KEY,
  SeuOffer,
  SeuOfferDetailsFormFields,
  SeuOfferFormGroups,
  SeuOfferOperatingConditionsFormGroups,
} from '@seu-types';
import { offerNumberValidator } from '@seu-validators';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OfferFormService {
  private _formGroups!: SeuOfferFormGroups;
  private _formInitialized = new BehaviorSubject<boolean>(false);

  constructor(private configFormService: ConfigFormService) {}

  get formGroups(): SeuOfferFormGroups {
    return this._formGroups;
  }

  public init(offer: SeuOffer): void {
    this._formGroups = this.getDefaultForm(offer);
    this._formInitialized.next(true);
  }

  get formInitialized$(): Observable<boolean> {
    return this._formInitialized.asObservable();
  }

  private getDefaultForm(offer: SeuOffer): SeuOfferFormGroups {
    // OFFER DETAILS
    // CREATE FORM WITH VALUES FROM CONFIG
    const offerDetails = new FormGroup<{
      [key in SeuOfferDetailsFormFields]: FormControl;
    }>({
      offerNumber: new FormControl(offer.offerNumber ?? null, [
        offerNumberValidator,
      ]),
      customer: new FormControl(offer.customer ?? null, [Validators.required]),
      name: new FormControl(offer.name ?? null, [Validators.required]),
      description: new FormControl(offer.description ?? null, []),
      industries: new FormControl(offer.industries ?? [], [
        Validators.required,
      ]),
      address: new FormControl(offer.address ?? null, []),
      address2: new FormControl(offer.address2 ?? null, []),
      zip: new FormControl(offer.zip ?? null, []),
      city: new FormControl(offer.city ?? null, []),
      countryCode: new FormControl(offer.countryCode ?? null, []),
    });

    // GLOBAL PARAMETERS
    const emptyGlobalParametersForm =
      this.configFormService.initFormGroupFromConfig('globalSettings');

    emptyGlobalParametersForm.addControl(
      'title',
      new FormControl(undefined, [Validators.required])
    );
    emptyGlobalParametersForm.addControl(
      'description',
      new FormControl(undefined, [])
    );

    const globalParameters: {
      [globalParameterId: string]: FormGroup<{
        [key: string]: FormControl | FormArray;
      }>;
    } = {};

    globalParameters[SEU_NEW_ID] = cloneDeep(emptyGlobalParametersForm);

    // INIT VALUES (GLOBAL PARAMETERS)
    offer.globalParameters.forEach((parameter) => {
      const globalParametersForm =
        this.configFormService.initFormGroupFromConfig(
          'globalSettings',
          parameter.inputs
        );
      globalParametersForm.addControl(
        'title',
        new FormControl(parameter.title ?? '', [Validators.required])
      );
      globalParametersForm.addControl(
        'description',
        new FormControl(parameter.description ?? '', [])
      );
      globalParameters[parameter.id] = cloneDeep(globalParametersForm);
    });

    // OPERATING CONDITIONS
    const emptyOperatingConditionsForm =
      this.configFormService.initFormGroupFromConfig('inputStream');

    emptyOperatingConditionsForm.addControl(
      'title',
      new FormControl(undefined, [Validators.required])
    );

    emptyOperatingConditionsForm.addControl(
      'categories',
      new FormControl(undefined, [])
    );

    emptyOperatingConditionsForm.addControl(
      'description',
      new FormControl(undefined, [])
    );

    const operatingConditions: SeuOfferOperatingConditionsFormGroups = {};

    // INIT VALUES (OPERATING CONDITIONS)
    offer.interconnections.forEach((interconnection) => {
      operatingConditions[interconnection.id] = {};
      operatingConditions[interconnection.id][SEU_NEW_ID] = cloneDeep(
        emptyOperatingConditionsForm
      );
      interconnection.operatingConditions.forEach((operatingCondition) => {
        const flatInputs = operatingCondition.inputStreams.flatMap(
          (inputStream) => {
            return inputStream.inputs;
          }
        );

        const operatingConditionForm =
          this.configFormService.initFormGroupFromConfig(
            'inputStream',
            flatInputs
          );

        operatingConditionForm.addControl(
          'title',
          new FormControl(operatingCondition.title ?? null, [
            Validators.required,
          ])
        );

        operatingConditionForm.addControl(
          'categories',
          new FormControl(operatingCondition.categories ?? [], [])
        );

        operatingConditionForm.addControl(
          'description',
          new FormControl(operatingCondition.description ?? null, [])
        );

        operatingConditions[interconnection.id][operatingCondition.id] =
          cloneDeep(operatingConditionForm);
      });
    });

    // OPEX
    const opexForm = this.configFormService.initFormGroupFromConfig(
      SEU_GLOBAL_VALUES_OPEX_CONFIG_KEY,
      offer.opex?.inputs
    );
    opexForm.patchValue({
      templateSearch: offer.opex?.presetId ?? null,
    });

    // EMISSION LIMITS
    const emissionLimitsForm = this.configFormService.initFormGroupFromConfig(
      SEU_GLOBAL_VALUES_EMISSION_LIMITS_CONFIG_KEY,
      offer.emissionLimits?.inputs
    );
    emissionLimitsForm.patchValue({
      templateSearch: offer.emissionLimits?.presetId ?? null,
    });

    // COMPONENTS
    const components: {
      [interconnectionId: string]: {
        [componentId: string]: FormGroup<{
          [key: string]: FormControl | FormArray;
        }>;
      };
    } = {};

    offer.interconnections.forEach((interconnection) => {
      components[interconnection.id] = {};
      interconnection.components.forEach((component) => {
        const componentForm = this.configFormService.initFormGroupFromConfig(
          component.componentType,
          component.inputs
        );
        componentForm.addControl(
          'name',
          new FormControl(component.name ?? '', [Validators.required])
        );
        componentForm.addControl(
          'componentType',
          new FormControl(component.componentType)
        );
        components[interconnection.id][component.id] = cloneDeep(componentForm);
      });
    });

    const offerFormGroups: SeuOfferFormGroups = {
      offerDetails: offerDetails,
      globalParameters: globalParameters,
      operatingConditions: operatingConditions,
      components: components,
      opex: opexForm,
      emissionLimits: emissionLimitsForm,
    };

    return offerFormGroups;
  }
}
